import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import DashboardStats from '../components/DashboardStats';
import Charts from '../components/Charts';
import HierarchyView from '../components/HierarchyView';
import ReadingsList from '../components/ReadingsList';
import DateFilter from '../components/DateFilter';
import './Dashboard.css';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const ClientDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState(() => {
    return searchParams.get('section') || 'dashboard';
  });
  const [stats, setStats] = useState(null);
  const [alcoholStats, setAlcoholStats] = useState(null);
  const [readingsOverTime, setReadingsOverTime] = useState([]);
  const [managerWiseReadings, setManagerWiseReadings] = useState([]);
  const [adulterationOverTime, setAdulterationOverTime] = useState([]);
  const [alcoholTrendsOverTime, setAlcoholTrendsOverTime] = useState([]);
  const [devicesOverTime, setDevicesOverTime] = useState([]);
  const [qualityDistribution, setQualityDistribution] = useState([]);
  const [morningEveningComparison, setMorningEveningComparison] = useState([]);
  const [dateFilter, setDateFilter] = useState({ startDate: '', endDate: '', timeFilter: '' });

  useEffect(() => {
    fetchAllData();
  }, [dateFilter]);

  // Sync activeSection with URL
  useEffect(() => {
    const sectionFromUrl = searchParams.get('section') || 'dashboard';
    if (sectionFromUrl !== activeSection) {
      setActiveSection(sectionFromUrl);
    }
  }, [searchParams]);

  // Update URL when activeSection changes
  const handleSectionChange = (section) => {
    setActiveSection(section);
    setSearchParams({ section });
  };

  const fetchAllData = () => {
    fetchStats();
    fetchAlcoholStats();
    fetchReadingsOverTime();
    fetchManagerWiseReadings();
    fetchAdulterationOverTime();
    fetchAlcoholTrendsOverTime();
    fetchDevicesOverTime();
    fetchQualityDistribution();
    fetchMorningEveningComparison();
  };

  const buildQueryParams = () => {
    const params = new URLSearchParams();
    if (dateFilter.startDate) params.append('startDate', dateFilter.startDate);
    if (dateFilter.endDate) params.append('endDate', dateFilter.endDate);
    if (dateFilter.timeFilter) params.append('timeFilter', dateFilter.timeFilter);
    return params.toString();
  };

  const fetchStats = async () => {
    try {
      const queryString = buildQueryParams();
      const url = `${BASE_URL}/api/dashboard/stats${queryString ? `?${queryString}` : ''}`;
      const response = await axios.get(url);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchAlcoholStats = async () => {
    try {
      const queryString = buildQueryParams();
      const url = `${BASE_URL}/api/dashboard/alcohol-stats${queryString ? `?${queryString}` : ''}`;
      const response = await axios.get(url);
      setAlcoholStats(response.data);
    } catch (error) {
      console.error('Error fetching alcohol stats:', error);
    }
  };

  const fetchReadingsOverTime = async () => {
    try {
      const queryString = buildQueryParams();
      const url = `${BASE_URL}/api/dashboard/readings-over-time${queryString ? `?${queryString}` : '?days=30'}`;
      const response = await axios.get(url);
      setReadingsOverTime(response.data);
    } catch (error) {
      console.error('Error fetching readings over time:', error);
    }
  };

  const fetchManagerWiseReadings = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/dashboard/manager-wise-readings`);
      setManagerWiseReadings(response.data);
    } catch (error) {
      console.error('Error fetching manager-wise readings:', error);
    }
  };

  const fetchAdulterationOverTime = async () => {
    try {
      const queryString = buildQueryParams();
      const url = `${BASE_URL}/api/dashboard/adulteration-over-time${queryString ? `?${queryString}` : '?days=30'}`;
      const response = await axios.get(url);
      setAdulterationOverTime(response.data);
    } catch (error) {
      console.error('Error fetching adulteration over time:', error);
    }
  };

  const fetchAlcoholTrendsOverTime = async () => {
    try {
      const queryString = buildQueryParams();
      const url = `${BASE_URL}/api/dashboard/alcohol-trends-over-time${queryString ? `?${queryString}` : '?days=30'}`;
      const response = await axios.get(url);
      setAlcoholTrendsOverTime(response.data);
    } catch (error) {
      console.error('Error fetching alcohol trends over time:', error);
    }
  };

  const fetchDevicesOverTime = async () => {
    try {
      const queryString = buildQueryParams();
      const url = `${BASE_URL}/api/dashboard/devices-over-time${queryString ? `?${queryString}` : '?days=30'}`;
      const response = await axios.get(url);
      setDevicesOverTime(response.data);
    } catch (error) {
      console.error('Error fetching devices over time:', error);
    }
  };

  const fetchQualityDistribution = async () => {
    try {
      const queryString = buildQueryParams();
      const url = `${BASE_URL}/api/dashboard/quality-distribution${queryString ? `?${queryString}` : ''}`;
      const response = await axios.get(url);
      setQualityDistribution(response.data);
    } catch (error) {
      console.error('Error fetching quality distribution:', error);
    }
  };

  const fetchMorningEveningComparison = async () => {
    try {
      const queryString = buildQueryParams();
      const url = `${BASE_URL}/api/dashboard/morning-evening-comparison${queryString ? `?${queryString}` : '?days=30'}`;
      const response = await axios.get(url);
      setMorningEveningComparison(response.data);
    } catch (error) {
      console.error('Error fetching morning evening comparison:', error);
    }
  };

  const handleDateChange = (dates) => {
    setDateFilter(dates);
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return (
          <div>
            <DateFilter onDateChange={handleDateChange} />
            <DashboardStats stats={stats} alcoholStats={alcoholStats} />
            <Charts 
              readingsOverTime={readingsOverTime} 
              managerWiseReadings={managerWiseReadings}
              adulterationOverTime={adulterationOverTime}
              alcoholTrendsOverTime={alcoholTrendsOverTime}
              devicesOverTime={devicesOverTime}
              qualityDistribution={qualityDistribution}
              morningEveningComparison={morningEveningComparison}
            />
          </div>
        );
      case 'hierarchy':
        return <HierarchyView onUpdate={fetchStats} />;
      case 'readings':
        return <ReadingsList />;
      default:
        return null;
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar activeSection={activeSection} setActiveSection={handleSectionChange} />
      <div className="dashboard-content">
        <div className="content-wrapper">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default ClientDashboard;

